import { useEffect, useRef, useState } from 'react';

import type { UserListItem } from '@/shared/api/types';
import { foldForMatch } from '@/shared/lib/strings';
import type { NavItem, NavSection } from '@/shared/registry/types';

const SECTION_RANK: Record<NavSection, number> = { primary: 0, main: 1, admin: 2 };

/** The palette lists navigation in the sidebar's order: sections first (administration last), then `order`. */
export const bySidebarOrder = (a: NavItem, b: NavItem): number =>
  SECTION_RANK[a.section ?? 'main'] - SECTION_RANK[b.section ?? 'main'] || a.order - b.order;

/** People shown in the palette (the directory is asked for a few more: some match only by the e-mail domain). */
export const PEOPLE_SHOWN = 6;

/**
 * Whether a person found by the directory search really matches the query. The directory also matches the e-mail
 * address, and every address shares the portal's domain: "Orbita" would list the first employees alphabetically.
 * Each query word must be in the name, position, department or the address' local part — or contain "@".
 */
export function personMatches(user: UserListItem, query: string): boolean {
  const local = user.email.split('@')[0] ?? '';
  const text = foldForMatch(
    [user.fullName, user.position, user.departmentName, local].filter(Boolean).join(' '),
  );
  const email = foldForMatch(user.email);
  return foldForMatch(query)
    .split(' ')
    .filter(Boolean)
    .every((word) => (word.includes('@') ? email.includes(word) : text.includes(word)));
}

/**
 * The palette's selected item, kept on an item that exists. cmdk re-selects the first item only when the selected
 * item is the *last* one to unmount in a render: when result groups swap several items at once (a query typed
 * while the previous one's actions were listed), its selection stays on an item that is gone, and Enter does
 * nothing. The list is watched, and a missing selection moves to the first item.
 */
export function useLiveSelection(): {
  /** Callback ref of the `CommandList`. */
  listRef: (element: HTMLElement | null) => void;
  value: string;
  onValueChange: (value: string) => void;
} {
  const [list, setList] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState('');
  const current = useRef(value);
  useEffect(() => {
    current.current = value;
  }, [value]);
  useEffect(() => {
    if (!list) return;
    const check = () => {
      const items = [...list.querySelectorAll<HTMLElement>('[cmdk-item]:not([aria-disabled="true"])')];
      if (items.some((item) => item.dataset.value === current.current)) return;
      setValue(items[0]?.dataset.value ?? '');
    };
    const observer = new MutationObserver(check);
    observer.observe(list, { childList: true, subtree: true });
    check();
    return () => observer.disconnect();
  }, [list]);
  return { listRef: setList, value, onValueChange: setValue };
}
